import React, { useState } from 'react';
import { languageName } from '../../platform-languages';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { PLAN_PAGE, TC_CHEAT } from '../../treatment/mockData';
import type { PlanProcedure } from '../../treatment/types';

/** The original ELVA texts, captured once, for "Have ELVA write it again". */
const PLAN_ORIGINAL: Record<number, { why: string; ifYouWait: string }> = Object.fromEntries(
  PLAN_PAGE.procedures.map((p) => [p.n, { why: p.why, ifYouWait: p.ifYouWait }]),
);

function readsTag(p: PlanProcedure): { label: string; tone: string } {
  if (p.source === 'edited') return { label: `Edited by ${p.sourceBy ?? 'staff'} · ${p.sourceDate ?? ''}`, tone: 'var(--money-green)' };
  if (p.source === 'standard') return { label: 'Standard text', tone: 'var(--text-muted)' };
  return { label: `Written by ELVA · ${p.sourceDate ?? ''}`, tone: 'var(--text-muted)' };
}

export interface TcCheatSheetRailProps {
  onLogCall?: () => void;
  onCopyLink?: () => void;
  onTextLink?: () => void;
  onPreviewPlan?: () => void;
  /** Two conflicting treatments on one tooth — the "Needs a choice" row (spec 48 §3.1). */
  needsChoice?: boolean;
}

/**
 * The four new cards on the Treatment cheat sheet (amendment 27 §7), in a 400px
 * right rail: **His plan page**, **Why call now**, **After the call**, and
 * **What's happened** — the record the old page didn't have.
 */
export function TcCheatSheetRail({ onLogCall, onCopyLink, onTextLink, onPreviewPlan, needsChoice }: TcCheatSheetRailProps) {
  const d = TC_CHEAT;
  const [showEnglish, setShowEnglish] = useState(true);
  const [copied, setCopied] = useState(false);
  const [texted, setTexted] = useState(false);
  // "What Todd reads" — the plan-page cards, editable here (spec 48 §3).
  const [reads, setReads] = useState<PlanProcedure[]>(PLAN_PAGE.procedures);
  const [editN, setEditN] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ why: string; ifYouWait: string }>({ why: '', ifYouWait: '' });
  const editing = editN !== null ? reads.find((p) => p.n === editN) ?? null : null;
  const moneyInDraft = draft.why.includes('$') || draft.ifYouWait.includes('$');

  const openReadEdit = (p: PlanProcedure) => { setEditN(p.n); setDraft({ why: p.why, ifYouWait: p.ifYouWait }); };
  const saveRead = () => {
    if (editing === null || moneyInDraft || draft.why.trim() === '') return;
    setReads(reads.map((p) => (p.n === editN ? { ...p, why: draft.why, ifYouWait: draft.ifYouWait, source: 'edited', sourceBy: 'Dana', sourceDate: '3 Oct' } : p)));
    setEditN(null);
  };

  const copyLink = () => {
    void navigator.clipboard.writeText(d.plan.link);
    onCopyLink?.();
    setCopied(true);
    window.setTimeout(() => { setCopied(false); }, 2000);
  };
  const textLink = () => {
    onTextLink?.();
    setTexted(true);
  };

  return (
    <div className="flex w-[400px] shrink-0 flex-col gap-4 max-[1200px]:w-full">
      {/* His plan page */}
      <RailCard title="His plan page" tag={planTag(d.plan.status)} tagTone={planTone(d.plan.status)}>
        <div className="flex items-center gap-2 rounded-[9px] bg-[var(--surface-sunken)] px-2.5 py-1.5">
          <span className="min-w-0 flex-1 truncate font-[var(--font-mono)] text-[11.5px] text-[var(--text-secondary)]">{d.plan.link}</span>
          <button type="button" onClick={copyLink} className="shrink-0 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">{copied ? 'Copied' : 'Copy'}</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={textLink} className="rounded-[8px] bg-[var(--brand-indigo)] px-2.5 py-1.5 text-[11.5px] font-semibold text-white">Text it to Todd</button>
          <button type="button" onClick={onPreviewPlan} className="rounded-[8px] border border-[var(--border-default)] px-2.5 py-1.5 text-[11.5px] font-semibold text-[var(--text-secondary)]">Preview</button>
        </div>
        {texted ? (
          <div className="mt-2 rounded-[8px] bg-[var(--money-green-tint)] px-2.5 py-1.5 text-[11px] font-semibold text-[var(--money-green)]">Link sent to (415) 555-0142</div>
        ) : null}
        <div className="mt-2.5 flex flex-col gap-0.5 text-[11px] text-[var(--text-muted)]">
          <span>{d.plan.opened}</span>
          <span>{d.plan.viewedXray}</span>
          <span>{d.plan.worksUntil}</span>
        </div>
        <p className="m-0 mt-2 text-[11px] leading-[1.5] text-[var(--text-muted)]">
          Todd enters his date of birth to open it. After 3 wrong tries it locks for an hour — Unlock shows here when it does.
        </p>
      </RailCard>

      {/* What Todd reads */}
      <RailCard title="What Todd reads">
        <p className="m-0 -mt-1 mb-2.5 text-[11px] leading-[1.5] text-[var(--text-muted)]">On his plan page. ELVA wrote it from the chart on 2 Sep.</p>
        {needsChoice === true ? (
          <div className="mb-2.5 rounded-[10px] border border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)] p-2.5">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--warn-amber)]">Needs a choice</div>
            <p className="m-0 mt-0.5 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]">A crown and an extraction are both planned on #14. Remove the one that isn’t planned in Dentrix. Until then, neither is on his plan page.</p>
          </div>
        ) : null}
        <div className="flex flex-col gap-2.5">
          {reads.map((p) => {
            const tag = readsTag(p);
            return (
              <div key={p.n} className="rounded-[10px] border border-[var(--border-default)] p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-muted)]">{p.n}</span>
                    <span className="text-[12px] font-semibold text-[var(--text-primary)]">{p.staffName}</span>
                  </span>
                  <button type="button" onClick={() => { openReadEdit(p); }} className="shrink-0 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Edit</button>
                </div>
                <span className="mt-1 inline-block rounded-[5px] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em]" style={{ color: tag.tone, background: '#F1F3F9' }}>{tag.label}</span>
                <div className="mt-2 text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">Why</div>
                <p className="m-0 mt-0.5 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]">{p.why}</p>
                {p.ifYouWait.trim() !== '' ? (
                  <>
                    <div className="mt-1.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">If you wait</div>
                    <p className="m-0 mt-0.5 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]">{p.ifYouWait}</p>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      </RailCard>

      {/* Why call now */}
      <RailCard title="Why call now">
        <div className="flex flex-col gap-2">
          {d.whyCallNow.map((s) => (
            <div key={s.label} className="flex items-baseline justify-between gap-2">
              <span className="text-[12.5px] text-[var(--text-secondary)]">
                {s.label}
                {s.strongest === true ? <span className="ml-1.5 rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--brand-indigo)]">strongest</span> : null}
              </span>
              <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{s.when}</span>
            </div>
          ))}
        </div>
        <div className="mt-2.5 rounded-[9px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">{d.campaignNote}</div>
      </RailCard>

      {/* After the call */}
      <RailCard title="After the call">
        <p className="m-0 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">
          Booked, call back, not now, or no answer. Logging it takes Todd off the Call list and tells the campaign what to do next.
        </p>
        <button type="button" onClick={onLogCall} className="mt-2 w-full rounded-[9px] bg-[var(--brand-indigo)] px-3 py-2 text-[12.5px] font-semibold text-white">Log the call</button>
      </RailCard>

      {/* What's happened */}
      <RailCard
        title="What’s happened"
        right={
          <button type="button" onClick={() => { setShowEnglish(!showEnglish); }} className="text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline">
            {languageName(d.language)} · {showEnglish ? 'showing English' : 'showing original'}
          </button>
        }
      >
        <div className="flex flex-col">
          {texted ? (
            <div className="border-t border-[var(--border-default)] py-2 first:border-0 first:pt-0">
              <div className="flex items-baseline gap-2">
                <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">just now</span>
                <span className="text-[11.5px] font-semibold text-[var(--text-primary)]">Text · plan link</span>
              </div>
              <p className="m-0 mt-0.5 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">You sent Todd his plan page.</p>
            </div>
          ) : null}
          {d.history.map((e, i) => {
            const original = !showEnglish && e.bodyFa !== undefined;
            const body = original ? e.bodyFa : e.body;
            return (
              <div key={`${e.at}-${String(i)}`} className="border-t border-[var(--border-default)] py-2 first:border-0 first:pt-0">
                <div className="flex items-baseline gap-2">
                  <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{e.at}</span>
                  <span className="text-[11.5px] font-semibold text-[var(--text-primary)]">{e.kind}</span>
                </div>
                {body !== undefined ? (
                  <p
                    className="m-0 mt-0.5 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]"
                    dir={original ? 'rtl' : undefined}
                  >
                    {body}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </RailCard>

      {editing !== null ? (
        <Modal
          title={`What Todd reads about his ${editing.costName.toLowerCase()}`}
          onClose={() => { setEditN(null); }}
          className="w-[min(560px,calc(100vw-32px))]"
          footer={
            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={() => { const o = PLAN_ORIGINAL[editing.n]; if (o) setDraft({ why: o.why, ifYouWait: o.ifYouWait }); }} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">Have ELVA write it again</button>
              <span className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={() => { setEditN(null); }}>Cancel</Button>
                <Button variant="primary" size="sm" disabled={moneyInDraft || draft.why.trim() === '' || draft.ifYouWait.trim() === ''} onClick={saveRead}>Save</Button>
              </span>
            </div>
          }
        >
          <p className="m-0 mb-3 text-[12px] leading-[1.5] text-[var(--text-muted)]">
            {(editing.title.split(' — ')[1] ?? '').replace(/^./, (c) => c.toUpperCase())}
            {(() => { const m = /#\d+/.exec(editing.staffName); return m ? ` (${m[0]})` : ''; })()}. He sees this on his plan page, in his language.
          </p>
          <div className="flex flex-col gap-4">
            <ReadField label="Why" value={draft.why} onChange={(v) => { setDraft({ ...draft, why: v }); }} />
            <ReadField label="If you wait" value={draft.ifYouWait} onChange={(v) => { setDraft({ ...draft, ifYouWait: v }); }} />
            {moneyInDraft ? (
              <div className="text-[11.5px] font-semibold text-[var(--alert-red)]">Leave prices out. The cost table shows them.</div>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** A 220-character field with a counter, for the "What Todd reads" editor. */
function ReadField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const MAX = 220;
  return (
    <label className="block">
      <span className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">{label}</span>
        <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{value.length}/{MAX}</span>
      </span>
      <textarea
        value={value}
        rows={3}
        maxLength={MAX}
        onChange={(e) => { onChange(e.target.value); }}
        className="w-full rounded-[9px] border border-[var(--border-default)] px-2.5 py-2 text-[12.5px] leading-[1.5] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
      />
    </label>
  );
}

function planTag(s: string): string {
  return s === 'active' ? 'Active' : s === 'locked' ? 'Locked' : s === 'expired' ? 'Expired' : 'No link yet';
}
function planTone(s: string): string {
  return s === 'active' ? 'var(--money-green)' : s === 'locked' ? 'var(--alert-red)' : 'var(--text-muted)';
}

function RailCard({ title, tag, tagTone, right, children }: { title: string; tag?: string; tagTone?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)] p-3.5 shadow-[var(--shadow-card)]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <span className="text-[12.5px] font-bold text-[var(--text-primary)]">{title}</span>
          <span className="rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-[1px] text-[8.5px] font-bold uppercase tracking-[0.06em] text-[var(--brand-indigo)]">New</span>
          {tag !== undefined ? <span className="rounded-[5px] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em]" style={{ color: tagTone, background: '#F1F3F9' }}>{tag}</span> : null}
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}
